'use client';

import type { MenuData, Template, TemplateThemeMode } from '@/app/dashboard/templates/components/types/template';
import { resolveTemplateEngine } from '@/app/dashboard/templates/components/lib/templateEngine';
import PagedFlipRenderer from './PagedFlipRenderer';
import { TemplateRenderer as LegacyTemplateRenderer } from './LegacyTemplateRenderer';

export function TemplateRenderer({
  template,
  data,
  showThemeToggle = false,
  themeTogglePosition = 'bottom-right',
  forcedThemeMode,
  onThemeModeChange,
  viewportOffsetPx = 0,
  guidePreview = false,
  bodyOnly = false,
  bottomChromeOffsetPx = 0,
}: {
  template: Template;
  data: MenuData;
  showThemeToggle?: boolean;
  themeTogglePosition?: 'bottom-right' | 'top-right';
  forcedThemeMode?: TemplateThemeMode;
  onThemeModeChange?: (mode: TemplateThemeMode) => void;
  viewportOffsetPx?: number;
  guidePreview?: boolean;
  /**
   * Extra pixels of fixed chrome below the embed (e.g. a host shell's bottom
   * rail). Forwarded to the paged-flip renderer so its swipe hint and page
   * indicator clear the rail on desktop. Unused by the legacy renderer.
   */
  bottomChromeOffsetPx?: number;
  /**
   * Render the menu as a BODY inside a host shell, never as a full-screen
   * surface of its own.
   *
   * The customer place shell (components/place/PlaceShell.tsx) owns the header,
   * session ribbon and bottom rail on every customer screen, and templates own
   * how dishes look inside the scroll region — that division is the whole point
   * of the redesign. A paged-flip template violates it by construction: it
   * takes the viewport, replaces scrolling with a pager, and hides the merchant's
   * own items behind page turns. On a 27-item menu that meant "Page 1 of 21 ·
   * Swipe to turn" and no visible way to order anything.
   *
   * So on the customer surface a paged template keeps its palette, fonts and
   * card styling — everything that makes it the merchant's — and loses the
   * pager. The designer and the dashboard preview pass this false and still get
   * the real flipbook, because there the page-turn IS the product.
   */
  bodyOnly?: boolean;
}) {
  const engine = resolveTemplateEngine(template);

  if (engine === 'paged-flip' && !bodyOnly) {
    return (
      <PagedFlipRenderer
        template={template}
        data={data}
        showThemeToggle={showThemeToggle}
        themeTogglePosition={themeTogglePosition}
        forcedThemeMode={forcedThemeMode}
        onThemeModeChange={onThemeModeChange}
        viewportOffsetPx={viewportOffsetPx}
        guidePreview={guidePreview}
        bottomChromeOffsetPx={bottomChromeOffsetPx}
      />
    );
  }

  return (
    <LegacyTemplateRenderer
      template={template}
      data={data}
      showThemeToggle={showThemeToggle}
      themeTogglePosition={themeTogglePosition}
      forcedThemeMode={forcedThemeMode}
      onThemeModeChange={onThemeModeChange}
    />
  );
}

export default TemplateRenderer;
